<script setup lang="ts">
/**
 * 〈市集 › 權利〉（`/plugin-grants`；批153 批A）——admin 以上。
 *
 * 全部私人 plugin 的權利（哪個客戶可以列出、下載、升級哪一支）。篩選狀態、客戶、plugin；
 * 指派、改到期日、撤銷。「發行者自己的組織」天生有權、不另建列，所以不會出現在這裡。
 * 到期**不改列**：狀態欄的「已到期」是依到期日算的。
 */
import { computed, onMounted, ref, watch } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as pm from '@/api/pluginMarket'
import type { MarketPlugin, PluginGrant } from '@/api/pluginMarket'
import { customerOptionLabel, isMarketCandidate } from '@/lib/customerLabel'
import {
  dateToExpiry,
  expiryToDate,
  GRANT_SOURCE_LABELS,
  GRANT_STATE_LABELS,
  GRANT_STATE_PILL,
  grantState,
  labelOf,
} from '@/lib/pluginMarket'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { fmtSeen } from '@/lib/staleness'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()

// 批186：三個過濾記住（`status` 的 `''`＝全部）；下面的 `watch` 會重載，清除只要改值。
const pf = usePersistedFilters(
  'plugin-grants',
  { status: 'active', pluginId: '', customerId: '' },
  { oneOf: { status: ['active', 'revoked', ''] } },
)
const { status, pluginId, customerId } = pf.refs
const { dirty: filtersDirty } = pf
const clearFilters = () => pf.clear()
const rows = ref<PluginGrant[]>([])
const cursor = ref<string | null>(null)
const loading = ref(false)
const loadError = ref('')
const plugins = ref<MarketPlugin[]>([])

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (typeof d === 'string') return d
  if (d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string') {
    return (d as { message: string }).message
  }
  return fallback
}
async function fail(e: unknown, fallback: string) {
  await dialogs.notify({ title: '操作失敗', message: errText(e, fallback) })
}

/** 請求序號（R3-4）：換條件時舊條件的慢回應一律丟掉。 */
let seq = 0
async function load(reset: boolean) {
  if (!reset && (loading.value || !cursor.value)) return
  const my = ++seq
  loading.value = true
  try {
    const page = await pm.listGrants({
      status: status.value || undefined,
      customerId: customerId.value || undefined,
      pluginId: pluginId.value || undefined,
      cursor: reset ? null : cursor.value,
      limit: 50,
    })
    if (my !== seq) return
    rows.value = reset ? page.items : [...rows.value, ...page.items]
    cursor.value = page.next_cursor
    loadError.value = ''
  } catch (e) {
    if (my !== seq) return
    loadError.value = errText(e, '讀取權利清單失敗')
  } finally {
    if (my === seq) loading.value = false
  }
}
watch([status, customerId, pluginId], () => {
  // 換條件：清空清單與游標，再讀第一頁。
  rows.value = []
  cursor.value = null
  void load(true)
})

onMounted(async () => {
  void store.ensureCustomers().catch(() => {})
  // 私人 plugin 的下拉（指派與篩選用）；數量是個位數到數百，一頁載完。
  void pm
    .listPlugins({ limit: 200 })
    .then((r) => (plugins.value = r.items))
    .catch(() => {})
  await load(true)
})

const privatePlugins = computed(() => plugins.value.filter((p) => p.visibility === 'private'))
/**
 * 指派的客戶候選：平台（SaaS）客戶不列（不使用市集；後端 409 `platform_managed_customer`）。
 * 篩選用的下拉維持全部客戶——舊資料要查得到。
 */
const assignCustomers = computed(() => store.customers.filter(isMarketCandidate))

// ---------------------------------------------------------------------------
// 指派／改到期日
// ---------------------------------------------------------------------------
const formOpen = ref(false)
const formBusy = ref(false)
const editing = ref<PluginGrant | null>(null)
const form = ref({ pluginId: '', customerId: '', expires: '', note: '' })
function openAssign() {
  editing.value = null
  // R3-11：篩選條件選的可能是公開 plugin（指派只用在私人的）——不在私人清單裡就不預填。
  const prefill = privatePlugins.value.some((p) => p.id === pluginId.value) ? pluginId.value : ''
  // 篩選選的客戶可能是平台客戶（不在指派候選裡）——同理不預填。
  const prefillCustomer = assignCustomers.value.some((c) => c.id === customerId.value) ? customerId.value : ''
  form.value = { pluginId: prefill, customerId: prefillCustomer, expires: '', note: '' }
  formOpen.value = true
}
/** 修改前的到期日（`<input type="date">` 的值）：只有改了才送 `expires_at`。 */
const originalExpires = ref('')
function openEdit(g: PluginGrant) {
  editing.value = g
  form.value = { pluginId: g.plugin_id, customerId: g.customer_id, expires: expiryToDate(g.expires_at), note: g.note ?? '' }
  originalExpires.value = form.value.expires
  formOpen.value = true
}
const formInvalid = computed(() => {
  if (editing.value) return null
  if (!form.value.pluginId) return '請選擇擴充'
  if (!form.value.customerId) return '請選擇客戶'
  return null
})
async function save() {
  if (formInvalid.value) return
  formBusy.value = true
  try {
    if (editing.value) {
      // R3-11：到期日沒動就不送 `expires_at`——只改備註時不該被到期日檢查擋下，
      // 也不該把原本的到期時刻挪到當天 23:59:59。
      const body: { expires_at?: string | null; note: string | null } = { note: form.value.note.trim() || null }
      if (form.value.expires !== originalExpires.value) body.expires_at = dateToExpiry(form.value.expires)
      await pm.updateGrant(editing.value.id, body)
    } else {
      await pm.upsertGrant({
        plugin_id: form.value.pluginId,
        customer_id: form.value.customerId,
        expires_at: dateToExpiry(form.value.expires),
        note: form.value.note.trim() || null,
      })
    }
    formOpen.value = false
    await load(true)
  } catch (e) {
    await fail(e, editing.value ? '修改權利失敗' : '指派失敗')
  } finally {
    formBusy.value = false
  }
}

async function revoke(g: PluginGrant) {
  const reason = await dialogs.prompt({
    title: `撤銷「${g.customer_name ?? g.customer_id}」對「${g.plugin_name ?? g.plugin_key}」的權利`,
    message: '撤銷後這個客戶列不出、下載不到、也升級不了這個擴充；已安裝的照常運作。',
    label: '撤銷理由（選填，會記進稽核）',
    maxLength: 1000,
    danger: true,
    confirmText: '撤銷',
  })
  if (reason === null) return
  try {
    await pm.revokeGrant(g.id, reason)
    await load(true)
  } catch (e) {
    await fail(e, '撤銷失敗')
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>權利</h1>
      <span class="count">{{ rows.length }}{{ cursor ? '+' : '' }}</span>
      <span class="spacer" />
      <select v-model="status" aria-label="狀態">
        <option value="active">有效（含已到期）</option>
        <option value="revoked">已撤銷</option>
        <option value="">全部</option>
      </select>
      <select v-model="pluginId" aria-label="擴充">
        <option value="">全部擴充</option>
        <option v-for="p in plugins" :key="p.id" :value="p.id">{{ p.name }}（{{ p.key }}）</option>
      </select>
      <select v-model="customerId" aria-label="客戶">
        <option value="">全部客戶</option>
        <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c) }}</option>
      </select>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
      <UiButton variant="primary" @click="openAssign">＋ 指派</UiButton>
    </div>

    <p class="subtle intro">
      私人擴充的權利：被指派的客戶可以在自己的站台列出、下載、升級那個擴充。發行者自己的組織天生有權，不會列在這裡。
      撤銷或到期只停止列出、下載、升級——已安裝的照常運作。
    </p>

    <p v-if="loadError" class="err">{{ loadError }}</p>
    <div class="card">
      <div class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table sticky-acts">
          <thead>
            <tr><th>擴充</th><th>客戶</th><th class="col-state">狀態</th><th>到期</th><th>來源</th><th>備註</th><th>指派</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="g in rows" :key="g.id" :class="{ gone: grantState(g) !== 'effective' }">
              <td class="wide">
                <RouterLink :to="{ name: 'plugin-detail', params: { id: g.plugin_id } }">{{ g.plugin_name ?? '—' }}</RouterLink>
                <div>
                  <code class="mono sub">{{ g.plugin_key }}</code>
                  <!-- 窄螢幕：狀態欄會被釘在右側的操作欄蓋住——改成跟在 key 後面（寬螢幕隱藏；ui.css）。 -->
                  <span class="status-pill state-inline" :class="GRANT_STATE_PILL[grantState(g)]">
                    {{ GRANT_STATE_LABELS[grantState(g)] }}
                  </span>
                </div>
                <div v-if="g.revoke_reason" class="subtle sub state-inline">{{ g.revoke_reason }}</div>
              </td>
              <td class="wide">{{ g.customer_name ?? g.customer_id }}</td>
              <td class="nowrap col-state">
                <span class="status-pill" :class="GRANT_STATE_PILL[grantState(g)]">{{ GRANT_STATE_LABELS[grantState(g)] }}</span>
                <div v-if="g.revoke_reason" class="subtle sub">{{ g.revoke_reason }}</div>
              </td>
              <td class="nowrap subtle">{{ g.expires_at ? fmtSeen(g.expires_at, { short: true }) : '不到期' }}</td>
              <td class="nowrap">{{ labelOf(GRANT_SOURCE_LABELS, g.source) }}</td>
              <td class="wide">{{ g.note ?? '—' }}</td>
              <td class="nowrap subtle">{{ fmtSeen(g.created_at, { short: true }) }}・{{ g.granted_by ?? '—' }}</td>
              <td class="acts-cell">
                <div v-if="g.status === 'active'" class="acts">
                  <UiButton size="sm" variant="secondary" @click="openEdit(g)">到期日</UiButton>
                  <UiButton size="sm" variant="danger" @click="revoke(g)">撤銷…</UiButton>
                </div>
              </td>
            </tr>
            <tr v-if="!loading && !rows.length">
              <td colspan="8" class="subtle empty-row">沒有符合條件的權利。</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="more-row">
        <span v-if="loading" class="subtle"><span class="spinner" /> 載入中…</span>
        <UiButton v-else-if="cursor" size="sm" @click="load(false)">載入更多</UiButton>
      </div>
    </div>

    <UiModal :open="formOpen" :title="editing ? '修改權利' : '指派私人擴充'" width="560px" @close="formOpen = false">
      <p v-if="!editing" class="subtle">已經有有效權利的客戶再指派一次＝就地更新到期日與備註。</p>
      <div class="form-grid">
        <label class="field">
          <span class="field-label">擴充</span>
          <select v-model="form.pluginId" :disabled="!!editing">
            <option value="">（請選擇）</option>
            <option v-for="p in editing ? plugins : privatePlugins" :key="p.id" :value="p.id">
              {{ p.name }}（{{ p.key }}）
            </option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">客戶</span>
          <select v-model="form.customerId" :disabled="!!editing">
            <option value="">（請選擇）</option>
            <!-- 修改時 select 是停用的，要能顯示原本那位客戶 → 用完整清單。 -->
            <option v-for="c in editing ? store.customers : assignCustomers" :key="c.id" :value="c.id">
              {{ customerOptionLabel(c) }}
            </option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">到期日（留空＝不到期）</span>
          <input v-model="form.expires" type="date" />
        </label>
        <label class="field">
          <span class="field-label">備註（選填）</span>
          <input v-model="form.note" maxlength="2000" placeholder="例：合約編號" />
        </label>
      </div>
      <p v-if="formInvalid" class="subtle">{{ formInvalid }}</p>
      <template #foot>
        <UiButton @click="formOpen = false">取消</UiButton>
        <UiButton variant="primary" :disabled="!!formInvalid" :loading="formBusy" @click="save">
          {{ editing ? '儲存' : '指派' }}
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.intro {
  margin: 0 0 var(--sp-3);
}
.err {
  margin: var(--sp-2) 0;
  color: var(--danger);
  font-size: var(--fs-12);
}
.mono {
  font-family: var(--font-mono);
}
.sub {
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.nowrap {
  white-space: nowrap;
}
/* 文字欄的最小寬度：窄螢幕時別擠成一字一行（表格本身在 .tbl-scroll 裡可橫向捲動）。 */
.wide {
  min-width: 140px;
}
tr.gone td {
  color: var(--fg-4);
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.more-row {
  display: flex;
  justify-content: center;
  margin-top: var(--sp-3);
}
</style>
